import { useEffect, useMemo, useRef, useState } from "react"
import { useSessionStorage } from "usehooks-ts"

import { AnalysisPage } from "~/components/design-system/pages"
import { storageLocationColumns } from "~/components/planning/dynamicTableData/DynamicTableColumns"
import { type PresetView } from "~/components/planning/presetView"
import InventoryKeyMetrics from "~/components/planning/InventoryKeyMetrics"
import ProductTableV2 from "~/components/planning/ProductTable/ProductTableV2"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import { INVENTORY_PAGE_TABLE_ID } from "~/constants"
import { useFilters } from "~/hooks/useFilters"
import { useVariants } from "~/hooks/useVariants"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import { filterVariants } from "~/utils/filter"
import { ProductTableColumn } from "~/utils/productTableUtils"
import { type AgGridReact } from "ag-grid-react"

const numberOfProductsPerView = 48

const defaultDisplayedColumnIds = [
  ProductTableColumn.VariantId,
  ProductTableColumn.Tags,
  ProductTableColumn.Flags,
  ProductTableColumn.Size,
  ProductTableColumn.Color,
  ProductTableColumn.Category,
  ProductTableColumn.StockLevel,
  ProductTableColumn.WeeksOnHand,
  ProductTableColumn.InventoryValue,
  ProductTableColumn.AvailabilityWithinLeadTime,
  ProductTableColumn.LocationAvailability,
  ProductTableColumn.LostSales,
  ProductTableColumn.OverstockQuantity,
]
type Props = {
  presetViews?: PresetView[]
  presetGroupId?: string
}

export default function Inventory({ presetViews, presetGroupId }: Props = {}) {
  const { filters } = useFilters()
  const gridRefInventory = useRef<AgGridReact<IVariantSummary>>(null)

  const [searchText] = useSessionStorage(INVENTORY_PAGE_TABLE_ID + "product-table-search-text", "")
  const [pageNumber, setPageNumber] = useState(1)

  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const rawVariants = useVariants()
  const { data: storageLocationsData, isPending: storageLocationsPending } =
    api.storageLocation.get.useQuery()

  const variants = useMemo(() => {
    return filterVariants(filters, rawVariants, searchText)
  }, [rawVariants, filters, searchText])

  const isFiltered = (variants?.length ?? 0) !== (rawVariants?.length ?? 0)

  useEffect(() => {
    if (variants !== undefined) {
      const totalPages = Math.ceil(variants.length / numberOfProductsPerView)
      const updatedPage = Math.min(pageNumber, totalPages) || 1
      if (pageNumber !== updatedPage) {
        setPageNumber(updatedPage)
      }
    }
  }, [pageNumber, variants])

  const storageColumns = useMemo(() => {
    if (storageLocationsData === undefined || storageLocationsData.length === 0) return []

    return storageLocationColumns(storageLocationsData.filter((s) => s.active))
  }, [variants, storageLocationsData])

  const storageColumnIds = useMemo(() => storageColumns.map((x) => x.colId ?? ""), [storageColumns])

  const hasStock = useMemo(() => rawVariants?.some((v) => v.stockLevel), [rawVariants])

  return (
    <AnalysisPage
      fillContent
      viewConfiguration={
        <ViewConfiguration
          tableId={INVENTORY_PAGE_TABLE_ID}
          presetViews={presetViews}
          presetGroupId={presetGroupId}
        />
      }
      inspector={
        hasStock ? (
          <InventoryKeyMetrics variants={variants ?? []} isFiltered={isFiltered} vertical />
        ) : undefined
      }
    >
      {/* The table builds a column per storage location; mounting before the query
          settles makes the column validator evict them from the current selection. */}
      {!storageLocationsPending && (
        <ProductTableV2
          gridRef={gridRefInventory}
          id={INVENTORY_PAGE_TABLE_ID}
          variants={variants ?? []}
          defaultColumnIdsToDisplay={[
            ...defaultDisplayedColumnIds,
            ...(storageColumnIds as ProductTableColumn[]),
          ]}
          defaultColumnIdsExcludedFromAutoAdd={storageColumnIds}
          warehouseId={selectedWarehouseId}
          extraColumns={storageColumns}
          masterDetail
          leftPinnedColumns={[ProductTableColumn.StockLevel, ProductTableColumn.InventoryValue]}
          enableSearch={true}
        />
      )}
    </AnalysisPage>
  )
}
